import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FiMail, FiPlus, FiTrash2, FiEdit2, FiCheck, FiX, FiRefreshCw, FiSearch } from "react-icons/fi";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints, urlEmailForwardingAccount } from "../config/api";
import { fetchAllOrgUsers, flattenOrgUsersForPicker } from "../utils/fetchOrgUsers";
import {
  isAlreadyAdded,
  normalizeEmail,
  parseBulkAddResponse,
  parseForwardingAccountsResponse,
  searchUsers,
  validateNewForwardingEmail,
} from "../utils/forwardingAccounts";
import Toast from "./Toast";

const DEV_BRANCH_MSG =
  "A different branch is deployed in the backend for testing — please reach out to the backend team.";

function apiError(error, fallback) {
  if (error?.response?.status === 404) return DEV_BRANCH_MSG;
  return getErrorMessage(error, fallback);
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const jsonHeaders = () => ({
  ...getAuthHeaders(),
  Accept: "*/*",
  "Content-Type": "application/json",
});

const ForwardingAccounts = ({ onClose, embedded = false }) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserIndex, setSelectedUserIndex] = useState(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  const pickerRef = useRef(null);

  const [summary, setSummary] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [newEmail, setNewEmail] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const [toast, setToast] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ id: Date.now(), type, message });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    let cancelled = false;

    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const orgGroups = await fetchAllOrgUsers(endpoints.orgsUsers);
        if (cancelled) return;
        setUsers(flattenOrgUsersForPicker(orgGroups));
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching org users", error);
        showToast("error", apiError(error, "Failed to fetch users."));
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [showToast]);

  const suggestions = useMemo(() => searchUsers(users, userSearch, 8), [users, userSearch]);

  const selectedUser = useMemo(
    () => users.find((u) => u.userIndex === selectedUserIndex) || null,
    [users, selectedUserIndex]
  );

  useEffect(() => {
    setActiveSuggestion(0);
  }, [userSearch]);

  // Close the suggestion list on an outside click so it never lingers.
  useEffect(() => {
    if (!suggestionsOpen) return undefined;
    const onPointerDown = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setSuggestionsOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [suggestionsOpen]);

  const selectUser = useCallback((user) => {
    setSelectedUserIndex(user.userIndex);
    setUserSearch("");
    setSuggestionsOpen(false);
  }, []);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      setSuggestionsOpen(false);
      return;
    }
    if (!suggestions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSuggestionsOpen(true);
      setActiveSuggestion((i) => (i + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSuggestionsOpen(true);
      setActiveSuggestion((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const user = suggestions[activeSuggestion] || suggestions[0];
      if (user) selectUser(user);
    }
  };

  /** GET the user's existing accounts. Called on every selection. */
  const loadAccounts = useCallback(
    async (userIndex) => {
      if (userIndex == null) {
        setSummary(null);
        return;
      }
      setLoadingAccounts(true);
      setLoadError("");
      try {
        const response = await axios.get(endpoints.emailForwardingAccounts, {
          params: { user_index: userIndex },
          headers: jsonHeaders(),
        });
        setSummary(parseForwardingAccountsResponse(response.data));
      } catch (error) {
        console.error("Error fetching forwarding accounts", error);
        const message = apiError(error, "Failed to load forwarding accounts.");
        setLoadError(message);
        showToast("error", message);
        setSummary(null);
      } finally {
        setLoadingAccounts(false);
      }
    },
    [showToast]
  );

  useEffect(() => {
    setEditingId(null);
    setNewEmail("");
    setNewLabel("");
    loadAccounts(selectedUserIndex);
  }, [selectedUserIndex, loadAccounts]);

  const handleAdd = async (event) => {
    event.preventDefault();
    if (selectedUserIndex == null) return;

    const validationError = validateNewForwardingEmail(newEmail, summary);
    if (validationError) {
      showToast("error", validationError);
      return;
    }

    const email = normalizeEmail(newEmail);
    setAdding(true);
    try {
      const response = await axios.post(
        endpoints.emailForwardingAccounts,
        {
          user_indexes: [selectedUserIndex],
          account_email: email,
          ...(newLabel.trim() ? { label: newLabel.trim() } : {}),
        },
        { headers: jsonHeaders() }
      );
      const { results, notFound } = parseBulkAddResponse(response.data);
      const result = results.find((r) => r?.user_index === selectedUserIndex) || results[0];
      const who = selectedUser?.name || selectedUser?.email || "this user";
      if (result?.status === "added" || isAlreadyAdded(result)) {
        setNewEmail("");
        setNewLabel("");
        showToast(
          "success",
          result.status === "added"
            ? `${email} added for ${who}.`
            : `${email} is already in ${who}'s list.`
        );
      } else if (result) {
        showToast("error", result.reason || `${email} was not added.`);
      } else if (notFound.length) {
        showToast("error", `These didn't match any Munshot user: ${notFound.join(", ")}`);
      }
      await loadAccounts(selectedUserIndex);
    } catch (error) {
      console.error("Error adding forwarding account", error);
      showToast("error", apiError(error, "Failed to add forwarding account."));
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (account) => {
    setEditingId(account.id);
    setEditLabel(account.label || "");
  };

  const handleSaveLabel = async (account) => {
    setSavingEdit(true);
    try {
      await axios.patch(
        urlEmailForwardingAccount(account.id),
        {
          user_index: selectedUserIndex,
          label: editLabel.trim() || null,
        },
        { headers: jsonHeaders() }
      );
      setEditingId(null);
      showToast("success", `Label updated for ${account.account_email}.`);
      await loadAccounts(selectedUserIndex);
    } catch (error) {
      console.error("Error updating forwarding account", error);
      showToast("error", apiError(error, "Failed to update label."));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleRemove = async (account) => {
    const name = account.label
      ? `${account.label} (${account.account_email})`
      : account.account_email;
    if (!window.confirm(`Remove ${name} from this user's forwarding accounts?`)) return;

    setRemovingId(account.id);
    try {
      await axios.delete(urlEmailForwardingAccount(account.id), {
        params: { user_index: selectedUserIndex },
        headers: jsonHeaders(),
      });
      showToast("success", `${account.account_email} removed.`);
      await loadAccounts(selectedUserIndex);
    } catch (error) {
      console.error("Error removing forwarding account", error);
      showToast("error", apiError(error, "Failed to remove forwarding account."));
    } finally {
      setRemovingId(null);
    }
  };

  const accounts = summary?.accounts ?? [];
  const maxAccounts = summary?.max_accounts;
  const atCapacity = typeof maxAccounts === "number" && accounts.length >= maxAccounts;
  const loginEmail = summary?.login_email || selectedUser?.email || "—";

  return (
    <div style={containerStyle}>
      {!embedded && (
        <button type="button" onClick={onClose} style={closeButtonStyle}>
          Close
        </button>
      )}

      {!embedded && (
        <header style={headerStyle}>
          <span style={headerIconStyle}>
            <FiMail aria-hidden="true" />
          </span>
          <div>
            <h2 style={titleStyle}>Allowed Forwarding Emails</h2>
            <p style={{ ...hintStyle, margin: 0 }}>
              Search for a user, then manage the addresses whose forwarded mail is attributed to them.
              Their login email is always allowed and cannot be removed.
            </p>
          </div>
        </header>
      )}

      <div style={pickerRowStyle} ref={pickerRef}>
        <label htmlFor="fa-user-search" style={labelStyle}>
          User
        </label>

        {selectedUser ? (
          <div style={selectedChipStyle}>
            <div style={{ minWidth: 0 }}>
              <span style={chipNameStyle}>
                {selectedUser.name || selectedUser.email || `User ${selectedUser.userIndex}`}
              </span>
              <span style={chipMetaStyle}>
                {[selectedUser.email, selectedUser.orgName].filter(Boolean).join(" · ") || "—"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setSelectedUserIndex(null)}
              style={ghostButtonStyle}
            >
              <FiX aria-hidden="true" />
              Change user
            </button>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            <div style={searchWrapStyle}>
              <FiSearch aria-hidden="true" style={searchIconStyle} />
              <input
                id="fa-user-search"
                type="text"
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setSuggestionsOpen(true);
                }}
                onFocus={() => setSuggestionsOpen(true)}
                onKeyDown={handleSearchKeyDown}
                placeholder={loadingUsers ? "Loading users…" : "Search by name, email or org"}
                disabled={loadingUsers}
                autoComplete="off"
                role="combobox"
                aria-expanded={suggestionsOpen && Boolean(userSearch.trim())}
                aria-controls="fa-user-suggestions"
                aria-autocomplete="list"
                style={searchInputStyle}
              />
            </div>

            {suggestionsOpen && userSearch.trim() && (
              <ul id="fa-user-suggestions" role="listbox" style={suggestionListStyle}>
                {suggestions.length === 0 ? (
                  <li style={emptyPaneStyle}>No users match that search.</li>
                ) : (
                  suggestions.map((user, index) => (
                    <li key={user.userIndex} role="option" aria-selected={index === activeSuggestion}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveSuggestion(index)}
                        onClick={() => selectUser(user)}
                        style={{
                          ...userRowStyle,
                          ...(index === activeSuggestion ? userRowSelectedStyle : null),
                        }}
                      >
                        <span style={userNameStyle}>
                          {user.name || user.email || `User ${user.userIndex}`}
                        </span>
                        <span style={userEmailStyle}>
                          {[user.name ? user.email : null, user.orgName]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        )}
      </div>

      <div>
        {/* ---- The selected user's allowed emails ---- */}
        <section style={rightPaneStyle}>
          {!selectedUser ? (
            <div style={placeholderStyle}>
              <FiMail aria-hidden="true" style={{ fontSize: 32, color: "#c7d2fe" }} />
              <p style={{ ...hintStyle, margin: 0 }}>
                Search for a user above to view and manage their allowed forwarding emails.
              </p>
            </div>
          ) : (
            <>
              <div style={rightHeaderStyle}>
                <div>
                  <h3 style={{ ...sectionTitleStyle, marginBottom: 4 }}>
                    {selectedUser.name || selectedUser.email || `User ${selectedUser.userIndex}`}
                  </h3>
                  <p style={{ ...hintStyle, margin: 0 }}>
                    {loginEmail} · {selectedUser.orgName || "—"}
                    {typeof maxAccounts === "number"
                      ? ` · ${accounts.length} / ${maxAccounts} accounts`
                      : ` · ${accounts.length} accounts`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => loadAccounts(selectedUserIndex)}
                  disabled={loadingAccounts}
                  style={{
                    ...secondaryButtonStyle,
                    opacity: loadingAccounts ? 0.6 : 1,
                    cursor: loadingAccounts ? "not-allowed" : "pointer",
                  }}
                >
                  <FiRefreshCw aria-hidden="true" />
                  Refresh
                </button>
              </div>

              <form onSubmit={handleAdd} style={addRowStyle}>
                <div style={{ flex: 2, minWidth: 220 }}>
                  <label htmlFor="fa-email" style={labelStyle}>
                    Email address
                  </label>
                  <input
                    id="fa-email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="analyst@fund.com"
                    style={inputStyle}
                    disabled={atCapacity}
                  />
                </div>
                <div style={{ flex: 2, minWidth: 180 }}>
                  <label htmlFor="fa-label" style={labelStyle}>
                    Label (optional)
                  </label>
                  <input
                    id="fa-label"
                    type="text"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="Work mailbox"
                    maxLength={255}
                    style={inputStyle}
                    disabled={atCapacity}
                  />
                </div>
                <button
                  type="submit"
                  disabled={adding || atCapacity}
                  style={{
                    ...buttonStyle,
                    opacity: adding || atCapacity ? 0.6 : 1,
                    cursor: adding || atCapacity ? "not-allowed" : "pointer",
                  }}
                >
                  <FiPlus aria-hidden="true" />
                  {adding ? "Adding…" : "Add email"}
                </button>
              </form>

              {atCapacity && (
                <p style={{ ...hintStyle, marginTop: 0 }}>
                  This user has reached the limit of {maxAccounts} forwarding accounts. Remove one to
                  add another.
                </p>
              )}

              {loadingAccounts ? (
                <p style={hintStyle}>Loading forwarding accounts…</p>
              ) : loadError ? (
                <p style={{ ...hintStyle, color: "#b91c1c" }}>{loadError}</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Email</th>
                        <th style={thStyle}>Label</th>
                        <th style={thStyle}>Added</th>
                        <th style={thStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={tdStyle}>{loginEmail}</td>
                        <td style={tdStyle}>
                          <span style={badgeStyle}>Login email</span>
                        </td>
                        <td style={tdStyle}>—</td>
                        <td style={{ ...tdStyle, color: "#6b7280" }}>Always allowed</td>
                      </tr>
                      {accounts.map((account) => {
                        const isEditing = editingId === account.id;
                        return (
                          <tr key={account.id}>
                            <td style={tdStyle}>{account.account_email}</td>
                            <td style={tdStyle}>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editLabel}
                                  onChange={(e) => setEditLabel(e.target.value)}
                                  maxLength={255}
                                  style={{ ...inputStyle, padding: "8px 10px" }}
                                  placeholder="Work mailbox"
                                  aria-label={`Label for ${account.account_email}`}
                                />
                              ) : (
                                account.label || <span style={{ color: "#9ca3af" }}>—</span>
                              )}
                            </td>
                            <td style={tdStyle}>{formatDate(account.created_at)}</td>
                            <td style={tdStyle}>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {isEditing ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleSaveLabel(account)}
                                      disabled={savingEdit}
                                      style={iconButtonStyle}
                                    >
                                      <FiCheck aria-hidden="true" />
                                      {savingEdit ? "Saving…" : "Save"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingId(null)}
                                      disabled={savingEdit}
                                      style={ghostButtonStyle}
                                    >
                                      <FiX aria-hidden="true" />
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => startEdit(account)}
                                      style={ghostButtonStyle}
                                    >
                                      <FiEdit2 aria-hidden="true" />
                                      Edit label
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemove(account)}
                                      disabled={removingId === account.id}
                                      style={dangerButtonStyle}
                                    >
                                      <FiTrash2 aria-hidden="true" />
                                      {removingId === account.id ? "Removing…" : "Remove"}
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {accounts.length === 0 && (
                        <tr>
                          <td style={{ ...tdStyle, color: "#6b7280" }} colSpan={4}>
                            No extra forwarding emails yet — add the first one above.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

const containerStyle = {
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
};

const closeButtonStyle = {
  height: "40px",
  padding: "0 16px",
  boxSizing: "border-box",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "600",
  marginBottom: "20px",
};

const headerStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: "16px",
  marginBottom: "20px",
};

const headerIconStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "44px",
  height: "44px",
  flexShrink: 0,
  borderRadius: "10px",
  backgroundColor: "#e0e7ff",
  color: "#3730a3",
  fontSize: "20px",
};

const titleStyle = {
  color: "#3730a3",
  fontSize: "22px",
  fontWeight: "700",
  margin: "0 0 6px",
};

const pickerRowStyle = {
  marginBottom: "20px",
  maxWidth: "520px",
};

const selectedChipStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "12px 14px",
  borderRadius: "6px",
  border: "1px solid #3730a3",
  backgroundColor: "#ffffff",
  boxSizing: "border-box",
};

const chipNameStyle = {
  display: "block",
  fontSize: "14px",
  fontWeight: "600",
  color: "#1f2937",
  wordBreak: "break-word",
};

const chipMetaStyle = {
  display: "block",
  fontSize: "12px",
  color: "#6b7280",
  wordBreak: "break-word",
};

const searchWrapStyle = {
  position: "relative",
};

const searchIconStyle = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: "#6b7280",
  pointerEvents: "none",
};

const searchInputStyle = {
  width: "100%",
  padding: "10px 12px 10px 36px",
  fontSize: "14px",
  backgroundColor: "#ffffff",
  border: "1px solid #3730a3",
  borderRadius: "6px",
  boxSizing: "border-box",
  color: "#1f2937",
  outline: "none",
};

const suggestionListStyle = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  zIndex: 20,
  margin: 0,
  padding: 0,
  listStyle: "none",
  maxHeight: "300px",
  overflowY: "auto",
  backgroundColor: "#ffffff",
  border: "1px solid #c7d2fe",
  borderRadius: "6px",
  boxShadow: "0 8px 20px rgba(17, 24, 39, 0.12)",
};

const userRowStyle = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "10px 16px",
  border: "none",
  borderBottom: "1px solid #eef2ff",
  borderLeft: "3px solid transparent",
  backgroundColor: "transparent",
  cursor: "pointer",
  boxSizing: "border-box",
};

const userRowSelectedStyle = {
  backgroundColor: "#ffffff",
  borderLeftColor: "#3730a3",
};

const userNameStyle = {
  display: "block",
  fontSize: "14px",
  fontWeight: "600",
  color: "#1f2937",
  wordBreak: "break-word",
};

const userEmailStyle = {
  display: "block",
  fontSize: "12px",
  color: "#6b7280",
  wordBreak: "break-word",
};

const emptyPaneStyle = {
  padding: "16px",
  color: "#6b7280",
  fontSize: "14px",
};

const rightPaneStyle = {
  width: "100%",
  padding: "24px",
  borderRadius: "8px",
  border: "1px solid #c7d2fe",
  backgroundColor: "#fafbff",
  boxSizing: "border-box",
};

const placeholderStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  minHeight: "260px",
  textAlign: "center",
};

const rightHeaderStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  alignItems: "flex-start",
  justifyContent: "space-between",
  marginBottom: "20px",
};

const addRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "16px",
  alignItems: "flex-end",
  marginBottom: "20px",
  paddingBottom: "20px",
  borderBottom: "1px solid #e0e7ff",
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  fontSize: "14px",
  backgroundColor: "#ffffff",
  border: "1px solid #3730a3",
  borderRadius: "6px",
  boxSizing: "border-box",
  color: "#1f2937",
  outline: "none",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontSize: "14px",
  fontWeight: "600",
  color: "#3730a3",
};

const buttonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 24px",
  fontSize: "14px",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "600",
};

const secondaryButtonStyle = {
  ...buttonStyle,
  padding: "10px 16px",
  backgroundColor: "#ffffff",
  color: "#3730a3",
  border: "1px solid #3730a3",
};

const ghostButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "8px 12px",
  fontSize: "13px",
  fontWeight: "600",
  backgroundColor: "#ffffff",
  color: "#3730a3",
  border: "1px solid #c7d2fe",
  borderRadius: "6px",
  cursor: "pointer",
};

const iconButtonStyle = {
  ...ghostButtonStyle,
  backgroundColor: "#3730a3",
  color: "#ffffff",
  border: "1px solid #3730a3",
};

const dangerButtonStyle = {
  ...ghostButtonStyle,
  color: "#b91c1c",
  border: "1px solid #fecaca",
};

const badgeStyle = {
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: "6px",
  backgroundColor: "#e0e7ff",
  color: "#3730a3",
  fontSize: "12px",
  fontWeight: "700",
};

const sectionTitleStyle = {
  color: "#3730a3",
  fontSize: "18px",
  fontWeight: "700",
  marginTop: 0,
  marginBottom: "16px",
};

const hintStyle = {
  color: "#6b7280",
  fontSize: "14px",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "14px",
};

const thStyle = {
  textAlign: "left",
  padding: "12px 14px",
  backgroundColor: "#3730a3",
  color: "#ffffff",
  fontWeight: "600",
  border: "1px solid #312e81",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px 14px",
  border: "1px solid #e5e7eb",
  color: "#1f2937",
  verticalAlign: "middle",
};

export default ForwardingAccounts;
